/**
 * TypeScript type definitions that mirror the C# Story models
 * These types match the data structures from NordicNeve.Models.Story
 */

// Enums

export enum NarrativeType {
  Dialogue = 'Dialogue',
  InnerThought = 'InnerThought',
  Narration = 'Narration',
  Action = 'Action',
}

export enum ConditionOperator {
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  GreaterThan = 'GreaterThan',
  LessThan = 'LessThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThanOrEqual = 'LessThanOrEqual',
  Contains = 'Contains',
  NotContains = 'NotContains',
}

export enum StateChangeOperation {
  Set = 'Set',
  Increment = 'Increment',
  Decrement = 'Decrement',
  Append = 'Append',
  Remove = 'Remove',
}

// Asset types

export interface Video {
  name: string;
  // Playback properties
  volume?: number;              // 0.0 to 1.0 (default: 1.0)
  loop?: boolean;               // Should video loop?
  autoPlay?: boolean;           // Start playing automatically?
  muted?: boolean;              // Start muted?
  fadeInDuration?: number;      // Fade in duration in milliseconds
  fadeOutDuration?: number;     // Fade out duration in milliseconds
  playbackRate?: number;        // Playback speed
  opacity?: number;             // Video opacity (0.0 to 1.0) for background dimming
}

export interface Audio {
  name: string;
  // Playback properties
  volume?: number;              // 0.0 to 1.0 (default: 1.0)
  loop?: boolean;               // Should audio loop?
  autoPlay?: boolean;           // Start playing automatically?
  fadeInDuration?: number;      // Fade in duration in milliseconds
  fadeOutDuration?: number;     // Fade out duration in milliseconds
  startDelay?: number;          // Delay before playing (ms)
  playbackRate?: number;        // Playback speed (0.5 = half speed, 2.0 = double speed)
}

export interface Image {
  name: string;
}

// Core story types

export interface Character {
  name: string;
  description: string;
  portrait?: Image;
  metadata?: Record<string, string>;
}

export interface Condition {
  stateKey: string;
  operator: ConditionOperator;
  value: unknown;
}

export interface StateChange {
  stateKey: string;
  operation: StateChangeOperation;
  value: unknown;
}

export interface PlayerChoice {
  id: string;
  text: string;
  nextNarrativeId?: string;
  nextSceneId?: string;
  stateChanges?: StateChange[];
  enableConditions?: Condition[];
}

export interface Narrative {
  id: string;
  type: NarrativeType;
  text: string;
  characterName?: string;
  audio?: Audio;
  choices?: PlayerChoice[];
  showConditions?: Condition[];
  nextNarrativeId?: string;
}

export interface Scene {
  id: string;
  title: string;
  description: string;
  characterNames?: string[];
  video?: Video;
  backgroundMusic?: Audio;
  backgroundImage?: Image;
  soundEffects?: Audio[];
  narratives: Narrative[];
  unlockConditions?: Condition[];
}

export interface Story {
  title: string;
  author?: string;
  description?: string;
  scenes: Scene[];
  characters: Character[];
  initialState?: Record<string, unknown>;
}

// Data transfer types (from Blazor via interop)

export interface AudioMetadata {
  url: string;
  volume?: number;              // 0.0 to 1.0 (default: 1.0)
  loop?: boolean;               // Should audio loop?
  autoPlay?: boolean;           // Start playing automatically?
  fadeInDuration?: number;      // Fade in duration in milliseconds
  fadeOutDuration?: number;     // Fade out duration in milliseconds
  startDelay?: number;          // Delay before playing (ms)
  playbackRate?: number;        // Playback speed (0.5 = half speed, 2.0 = double speed)
}

export interface VideoMetadata {
  url: string;
  volume?: number;              // 0.0 to 1.0 (default: 1.0)
  loop?: boolean;               // Should video loop?
  autoPlay?: boolean;           // Start playing automatically?
  muted?: boolean;              // Start muted?
  fadeInDuration?: number;      // Fade in duration in milliseconds
  fadeOutDuration?: number;     // Fade out duration in milliseconds
  startDelay?: number;          // Delay before playing (ms)
  playbackRate?: number;        // Playback speed
  opacity?: number;             // Video opacity (0.0 to 1.0) for dimming
}

export type OverlayAnimation =
  | 'slide-up-breath'   // Slides up from bottom with subtle breathing idle
  | 'fade'              // Simple fade in/out
  | 'zoom-in'           // Zoom from small to normal
  | 'none';             // Instant appearance

export interface FrameOverlay {
  frameAsset: string;           // Path to frame PNG (e.g., "polaroid_eira.png")
  animation?: OverlayAnimation; // Animation preset (default: "slide-up-breath")
  audioEnter?: string;          // Sound effect on enter (e.g., "fabric_rustle.mp3")
  audioExit?: string;           // Sound effect on exit
  contentPosition?: 'center' | 'fill' | 'contain'; // How content fits in frame
}

export interface ImageMetadata {
  url: string;
  opacity?: number;             // Image opacity (0.0 to 1.0) for overlay effects
  frame?: string;               // Simple: just frame asset name (uses defaults)
  overlay?: FrameOverlay;       // Detailed: full overlay configuration
}

export interface SceneData {
  id: string;
  title: string;
  description: string;
  videoUrl?: string;
  backgroundImageUrl?: string;
  backgroundMusicUrl?: string;
  soundEffects?: AudioMetadata[];
  characterNames?: string[];
  credits?: CreditsData;
}

export interface CreditsData {
  slides: SlideData[];
  backgroundMusic?: AudioMetadata;
}

export interface SlideData {
  text?: string;
  duration: number;
  image?: ImageMetadata;
  video?: VideoMetadata;
  effect?: string;
  position?: string;
}

export interface NarrativeData {
  id: string;
  type: string;
  text: string;
  characterName?: string;
  characterPortraitUrl?: string;
  audioUrl?: string;
  video?: VideoMetadata;
  image?: ImageMetadata;
  soundEffects?: AudioMetadata[];
  stopSceneSoundEffects?: string[];
  hasChoices: boolean;
  autoAdvance: boolean;
}

// Store types

export interface StoryState {
  currentScene: SceneData | null;
  currentNarrative: NarrativeData | null;
  availableChoices: PlayerChoice[];
  isStoryCompleted: boolean;
  isLoading: boolean;
}

export interface SettingsState {
  textSpeed: number; // 0-100
  autoAdvance: boolean;
  autoAdvanceDelay: number; // milliseconds
  musicVolume: number; // 0-100
  sfxVolume: number; // 0-100
  voiceVolume: number; // 0-100
  skipRead: boolean;
  showTypewriter: boolean;
}
